"use server";

const STRAPI_API_URL = process.env.STRAPI_API_URL;

export interface PasswordResetResult {
  success: boolean;
  error?: string;
}

/**
 * Request password reset - Strapi generates token and sends email directly
 */
export async function forgotPasswordAction(email: string): Promise<PasswordResetResult> {
  try {
    if (!STRAPI_API_URL) {
      return { success: false, error: "Server configuration error" };
    }

    if (!email) {
      return { success: false, error: "Email is required" };
    }

    const response = await fetch(`${STRAPI_API_URL}/api/saas-auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email.toLowerCase() }),
    });

    if (!response.ok) {
      // Always return success to prevent email enumeration
      console.error("Forgot password error:", await response.text());
    }

    // Always return success to prevent email enumeration
    return { success: true };
  } catch (error) {
    console.error("Forgot password error:", error);
    // Still return success to prevent email enumeration
    return { success: true };
  }
}

/**
 * Reset password using token
 */
export async function resetPasswordAction(
  token: string,
  password: string
): Promise<PasswordResetResult> {
  try {
    if (!STRAPI_API_URL) {
      return { success: false, error: "Server configuration error" };
    }

    if (!token || !password) {
      return { success: false, error: "Token and password are required" };
    }

    if (password.length < 8) {
      return { success: false, error: "Password must be at least 8 characters" };
    }
    if (!/[A-Z]/.test(password)) {
      return { success: false, error: "Password must contain at least one uppercase letter" };
    }
    if (!/[a-z]/.test(password)) {
      return { success: false, error: "Password must contain at least one lowercase letter" };
    }
    if (!/[0-9]/.test(password)) {
      return { success: false, error: "Password must contain at least one number" };
    }

    const response = await fetch(`${STRAPI_API_URL}/api/saas-auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.error?.message || "Failed to reset password. The link may have expired.",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Reset password error:", error);
    return { success: false, error: "An unexpected error occurred. Please try again." };
  }
}
